/**
 * Login Screen
 * Handles user authentication with email/password
 * Redesigned with pill-shaped inputs to match auth-screens-preview
 */

import React, { useState } from 'react';
import { Platform, ScrollView } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import {
  Box,
  VStack,
  HStack,
  Text,
  Pressable,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { colors, FS_SM, FS_MD } from '../theme';
import { TextLinkButton } from '../components/ui';
import { E2E_MODE } from '../config/constants';
import { PillInput } from '../components/auth/PillInput';
import { PillButton } from '../components/auth/PillButton';
import { useAppBannerStore } from '../hooks/useAppBanner';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

const FS_LOGO = 36;
const FS_FLAG = 20;

// Notification state for web
export type Notification = { title: string; message: string; type: 'error' | 'success' } | null;

export interface LoginScreenDisplayProps {
  email: string;
  password: string;
  isSubmitting: boolean;
  notification: Notification;
  onEmailChange: (text: string) => void;
  onPasswordChange: (text: string) => void;
  onLogin: () => void;
  onDismissNotification: () => void;
  onNavigateSignup: () => void;
  onNavigateForgot: () => void;
  isE2EMode?: boolean;
}

export function LoginScreenDisplay({
  email,
  password,
  isSubmitting,
  notification,
  onEmailChange,
  onPasswordChange,
  onLogin,
  onDismissNotification,
  onNavigateSignup,
  onNavigateForgot,
  isE2EMode,
}: LoginScreenDisplayProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
      >
        <Box flex={1} justifyContent="center" alignItems="center" px="$6" py="$10">
          {/* Web notification banner */}
          {Platform.OS === 'web' && notification && (
            <Pressable
              testID="login-button-dismiss"
              accessibilityLabel={t('common.dismiss')}
              accessibilityRole="button"
              onPress={onDismissNotification}
              style={{
                position: 'absolute',
                top: 20,
                left: 20,
                right: 20,
                zIndex: 100,
              }}
            >
              <Box
                p="$4"
                borderRadius="$lg"
                bg={notification.type === 'error' ? colors.error : colors.success}
              >
                <Text allowFontScaling={false} style={{ color: 'white', fontWeight: 'bold', ...ffs }} mb="$1">
                  {notification.title}
                </Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>
                  {notification.message}
                </Text>
              </Box>
            </Pressable>
          )}

          {/* Logo */}
          <VStack space="sm" mb="$4" alignItems="center" mt="$5">
            <Text allowFontScaling={false} style={{ fontSize: FS_LOGO * fontScale, fontWeight: '800', color: colors.primary, ...ffs }}>
              <Text allowFontScaling={false} style={{ fontSize: FS_LOGO * fontScale, ...ffs }}>{'🌿 '}</Text>
              {t('auth.login.appName')}
            </Text>
          </VStack>

          {/* Subtitle */}
          <Text
            testID="login-subtitle"
            accessibilityRole="text"
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
            mb="$8"
          >
            {t('auth.login.subtitle')}
          </Text>

          {/* Language Flags */}
          <HStack space="sm" alignItems="center" mb="$8">
            <Text allowFontScaling={false} style={{ fontSize: FS_FLAG * fontScale, ...ffs }}>{'\u{1F1EA}\u{1F1F8}'}</Text>
            <Text allowFontScaling={false} style={{ color: colors.primary, fontWeight: '600', fontSize: 16 * fontScale, ...ffs }}>{'\u2192'}</Text>
            <Text allowFontScaling={false} style={{ fontSize: FS_FLAG * fontScale, ...ffs }}>{'\u{1F1FA}\u{1F1F8}'}</Text>
          </HStack>

          {/* Form */}
          <Box w="$full" maxWidth={400}>
            <PillInput
              label={t('auth.login.emailLabel')}
              icon={'\u2709\uFE0F'}
              testID="login-input-email"
              accessibilityLabel={t('auth.login.emailAccessibility')}
              placeholder="you@example.com"
              value={email}
              onChangeText={onEmailChange}
              autoCapitalize="none"
              keyboardType="email-address"
              editable={!isSubmitting}
              textContentType={isE2EMode ? 'none' : 'emailAddress'}
              autoComplete={isE2EMode ? 'off' : 'email'}
            />

            <PillInput
              label={t('auth.login.passwordLabel')}
              icon={'\u{1F512}'}
              isPassword
              forceShowPassword={isE2EMode}
              testID="login-input-password"
              accessibilityLabel={t('auth.login.passwordAccessibility')}
              placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
              value={password}
              onChangeText={onPasswordChange}
              editable={!isSubmitting}
              textContentType={isE2EMode ? 'none' : 'password'}
              autoComplete={isE2EMode ? 'off' : 'password'}
            />

            {/* Forgot Password Button */}
            <PillButton
              testID="login-link-forgot"
              title={t('auth.login.forgotPasswordButton')}
              variant="secondary"
              onPress={onNavigateForgot}
              isDisabled={isSubmitting}
            />

            {/* Sign In Button */}
            <PillButton
              testID="login-button-submit"
              title={t('auth.login.signInButton')}
              onPress={onLogin}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            />

            {/* Sign Up Link */}
            <HStack justifyContent="center" mt="$2" space="xs">
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('auth.login.dontHaveAccount')}
              </Text>
              <TextLinkButton testID="login-link-signup" accessibilityLabel={t('auth.login.signUpAccessibility')} accessibilityRole="link" label={t('auth.login.signUpLink')} onPress={onNavigateSignup} disabled={isSubmitting} />
            </HStack>
          </Box>
        </Box>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export function LoginScreen() {
  const navigation = useNavigation('Login');
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isSubmitting } = useAuthStore();
  const [notification, setNotification] = useState<Notification>(null);

  const showBanner = useAppBannerStore((s) => s.showBanner);

  // Show message via banner (works on all platforms)
  const showMessage = (title: string, message: string, type: 'error' | 'success' = 'error') => {
    if (Platform.OS === 'web') {
      setNotification({ title, message, type });
      setTimeout(() => setNotification(null), 5000);
    } else {
      showBanner({ message, type });
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      showMessage(t('common.error'), t('auth.login.validationError'));
      return;
    }

    try {
      await login(email, password);
    } catch (_error: unknown) {
      const errorMessage = useAuthStore.getState().error || 'Login failed';
      showMessage(t('auth.login.loginFailedTitle'), errorMessage);
    }
  };

  // E2E mode: disable password autofill to prevent iOS "Save Password?" dialog
  const isE2EMode = E2E_MODE;

  return (
    <LoginScreenDisplay
      email={email}
      password={password}
      isSubmitting={isSubmitting}
      notification={notification}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onLogin={handleLogin}
      onDismissNotification={() => setNotification(null)}
      onNavigateSignup={() => navigation.navigate('Signup')}
      onNavigateForgot={() => navigation.navigate('ForgotPassword')}
      isE2EMode={isE2EMode}
    />
  );
}
